/**
 * Life4Earth V2 — Apply Pre-Flight Template to a Project
 *
 * Copies template items into a project as PreflightItems.
 * No live binding — the team can freely modify, add, or remove items.
 */

import type { PreflightItem, PreflightTemplate } from './preflightTypes';

/**
 * Generate a proper RFC 4122 v4 UUID.
 * The backend stores item_guid as a PostgreSQL UUID column,
 * so non-UUID strings (e.g. "pf-tpl-...") will fail on insert.
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto?.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * Apply a template to a project, generating PreflightItems from the
 * template's item definitions. Returns new items ready to be merged
 * into the project's pre-flight list.
 *
 * @param template  The template to apply
 * @param projectGuid  The target project GUID
 * @param createdBy  The user GUID who is applying the template
 * @param startSortOrder  Starting sort_order for the new items (to append after existing)
 */
export function applyPreflightTemplate(
  template: PreflightTemplate,
  projectGuid: string,
  createdBy: string,
  startSortOrder: number = 1,
): PreflightItem[] {
  const now = new Date().toISOString();

  return template.items.map((ti, idx) => ({
    item_guid: generateUUID(),
    project_guid: projectGuid,
    phase: ti.phase ?? 'preparation',
    category: ti.category,
    label: ti.label,
    description: ti.description,
    requirement_type: ti.requirement_type,
    target_quantity: ti.target_quantity ?? null,
    unit: ti.unit ?? null,
    current_quantity: 0,
    is_fulfilled: false,
    visibility: ti.visibility,
    mission_subkpi_key: ti.mission_subkpi_key ?? null,
    tags: ti.tags ? [...ti.tags] : [],
    sort_order: startSortOrder + idx,
    created_by: createdBy,
    created_at: now,
    updated_at: now,
    source_template_guid: template.template_guid,
  }));
}
